import type { BingoPayoutMap, BingoWin } from './types';

/** Stable ordering of win kinds — emitted in this order so callers can
 * react to "highest first" on detection. */
const KIND_ORDER: BingoWin['kind'][] = ['blackout', 'four-corners', 'diagonal', 'column', 'row'];

/**
 * Pure win detection. Walks the 12 standard bingo patterns (5 rows, 5
 * columns, 2 diagonals) plus four-corners and full-card blackout, returns
 * every pattern that's currently complete on the marked grid.
 *
 * <p>{@code marked[col][row]} is true when the player has marked that cell.
 * The centre {@code [2][2]} should always be marked (FREE square) on the
 * caller side.
 *
 * <p>Returns wins ordered by kind: blackout > four-corners > diagonal >
 * column > row, so callers can show the highest first.
 */
export function checkBingoWins(
  marked: readonly (readonly boolean[])[],
  payouts: BingoPayoutMap,
): BingoWin[] {
  const wins: BingoWin[] = [];

  // Rows.
  for (let row = 0; row < 5; row++) {
    const cells: [number, number][] = [];
    let allMarked = true;
    for (let col = 0; col < 5; col++) {
      cells.push([col, row]);
      if (!marked[col]?.[row]) {
        allMarked = false;
      }
    }
    if (allMarked) wins.push({ kind: 'row', cells, multiplier: payouts.row });
  }

  // Columns.
  for (let col = 0; col < 5; col++) {
    const cells: [number, number][] = [];
    let allMarked = true;
    for (let row = 0; row < 5; row++) {
      cells.push([col, row]);
      if (!marked[col]?.[row]) {
        allMarked = false;
      }
    }
    if (allMarked) wins.push({ kind: 'column', cells, multiplier: payouts.column });
  }

  // Diagonals.
  const tlbr: [number, number][] = [];
  const trbl: [number, number][] = [];
  let tlbrMarked = true;
  let trblMarked = true;
  for (let i = 0; i < 5; i++) {
    tlbr.push([i, i]);
    trbl.push([i, 4 - i]);
    if (!marked[i]?.[i]) tlbrMarked = false;
    if (!marked[i]?.[4 - i]) trblMarked = false;
  }
  if (tlbrMarked) wins.push({ kind: 'diagonal', cells: tlbr, multiplier: payouts.diagonal });
  if (trblMarked) wins.push({ kind: 'diagonal', cells: trbl, multiplier: payouts.diagonal });

  // Four corners.
  const corners: [number, number][] = [
    [0, 0],
    [4, 0],
    [0, 4],
    [4, 4],
  ];
  if (corners.every(([c, r]) => marked[c]?.[r])) {
    wins.push({ kind: 'four-corners', cells: corners, multiplier: payouts['four-corners'] });
  }

  // Blackout (full card).
  let blackout = true;
  const allCells: [number, number][] = [];
  for (let c = 0; c < 5; c++) {
    for (let r = 0; r < 5; r++) {
      allCells.push([c, r]);
      if (!marked[c]?.[r]) blackout = false;
    }
  }
  if (blackout) wins.push({ kind: 'blackout', cells: allCells, multiplier: payouts.blackout });

  // Sort by KIND_ORDER for stable highest-first.
  return wins.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
}
